import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useHistory } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import SortIcon from '@mui/icons-material/Sort';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LinkedProviderPopover from 'components/MenuNew/LinkedProviderPopover';
import Private from 'components/Private';
import { v4 as uuidv4 } from 'uuid';
import variables from 'styles/_variables.scss';
import { ItemSortModal } from 'components/MenuNew/SortingModals';
import { getCategoryProductsV2 } from 'redux/actions/Menu/products';

const CategoryItem = (props) => {
  const {
    category,
    menu,
    indexCat,
    searchQuery,
    setFilteredData = () => {},
  } = props;
  const { isCollapsed = false, onToggle } = props;
  const { _id, name, products } = category;
  const [itemSortModalOpen, setItemSortModalOpen] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  // no-op

  const filterData = (query, data) => {
    const allData = [];
    if (!query) {
      return data;
    } else {
      const result = data?.filter((items) => {
        if (items.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          allData.push(items);
          return items;
        }
      });

      if (!allData?.length) {
        setFilteredData(allData);
      }
      return result;
    }
  };

  const filtersData = useMemo(
    () => filterData(searchQuery, products),
    [searchQuery, products]
  );
  const filtersDataCategory = useMemo(
    () => filterData(searchQuery, menu.categories),
    [searchQuery]
  );

  useEffect(() => {
    if (searchQuery && filtersData && filtersDataCategory) {
      const allFoundData = [...filtersData, ...filtersDataCategory];
      if (allFoundData?.length) {
        setFilteredData(allFoundData);
      }
    }
  }, [searchQuery, setFilteredData]);

  const handleOpenCategory = (category) => {
    if (category._id) {
      history.push({
        search: `?entityType=category&cid=${category._id}&mode=edit`,
      });
    } else {
      history.push({
        search: `?entityType=category&cid=${uuidv4()}&mode=create`,
      });
    }
  };

  const handleOpenItemSortModal = () => {
    setItemSortModalOpen(true);
  };

  const handleCloseItemSortModal = () => {
    setItemSortModalOpen(false);
  };

  // Ensure products are loaded when the header becomes visible
  useEffect(() => {
    const hasProductsArray = Array.isArray(category?.products);
    const hasLoadedProducts = hasProductsArray && category.products.length > 0;
    const shouldHaveProducts = (category?.productsCount || 0) > 0;
    if (!hasLoadedProducts && shouldHaveProducts) {
      dispatch(getCategoryProductsV2(category._id));
    }
  }, [
    dispatch,
    category?._id,
    category?.products?.length,
    category?.productsCount,
  ]);

  return (
    <>
      <Box className="nav-item-draggable category-wrapper">
        <Box
          sx={{
            background: category?.bulkInfo?.isSaved
              ? 'white'
              : variables.backgroundBulk,
          }}
        >
          <ListItem
            className="overview-items-main"
            id={`items-${indexCat}`}
            sx={{
              mb: 0,
              px: 3,
            }}
          >
            <ListItemIcon sx={{ display: 'flex', alignItems: 'center' }}>
              <div>
                <LinkedProviderPopover
                  id={_id}
                  overrideStyle={{
                    background: category?.bulkInfo?.isSaved
                      ? ''
                      : variables.borderBulk,
                  }}
                  connectedProviders={category.connectedProviders}
                  target="category"
                  targetData={category}
                  targetConnectedProviders={menu.connectedProviders}
                />
              </div>
            </ListItemIcon>
            <IconButton
              size="small"
              onClick={onToggle}
              sx={{ mx: 1 }}
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? (
                <ExpandMore fontSize="medium" />
              ) : (
                <ExpandLess fontSize="medium" />
              )}
            </IconButton>
            <ListItemText
              onClick={() => handleOpenCategory(category)}
              primary={
                <Link to="?entityType=category">
                  <Box display="flex">
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#141414',
                        fontWeight: 600,
                      }}
                      variant="body2"
                    >
                      {name}
                    </Typography>
                    <Typography
                      sx={{ color: '#434343', fontSize: '12px', ml: 1 }}
                    >{`(Number of items: ${category?.productsCount} )`}</Typography>
                  </Box>
                </Link>
              }
            />
            {/* Sort Items Button */}
            {category?.productsCount > 1 ? (
              <Private permission="MENU_EDIT">
                <Tooltip title="Sort items in this category">
                  <IconButton
                    size="small"
                    onClick={handleOpenItemSortModal}
                    disabled={
                      !filtersData?.length || category?.productsCount === 1
                    }
                    sx={{
                      color: '#9E9EAD',
                      '&:hover': {
                        backgroundColor: 'rgba(158, 158, 173, 0.04)',
                      },
                    }}
                  >
                    <SortIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Private>
            ) : null}
          </ListItem>
        </Box>
      </Box>

      {/* Item Sort Modal */}
      <ItemSortModal
        open={itemSortModalOpen}
        onClose={handleCloseItemSortModal}
        category={category}
        products={filtersData || []}
      />
    </>
  );
};

export default CategoryItem;
